/**
 * Google Analytics 4 loader (Story 30.17 wiring).
 *
 * GA4 is loaded client-side via gtag.js, in Google Consent Mode v2 Advanced:
 * the default-DENIED consent state is pushed BEFORE the library loads (reusing
 * the compliance/consent-mode helpers), so GA4 sends only cookieless pings —
 * GDPR-safe, no cookie banner strictly required — until a CMP grants consent.
 *
 * Dormant until `VITE_GA4_MEASUREMENT_ID` holds a real `G-XXXXXXX` id, so nothing
 * loads in dev/preview. This is the single analytics tag for now, standalone
 * from the (dormant) GTM path in `compliance/gtm.ts`; when GTM is later
 * provisioned it can take over.
 */
import { gtag, pushConsentDefaultDenied } from "../compliance/consent-mode";

export const GA4_MEASUREMENT_ID: string = import.meta.env.VITE_GA4_MEASUREMENT_ID ?? "";

const GA4_ID_PATTERN = /^G-[A-Z0-9]+$/;

/** True for a syntactically valid GA4 measurement id (pure, testable). */
export function isValidGa4Id(id: string): boolean {
  return GA4_ID_PATTERN.test(id.trim());
}

/** True once a real GA4 measurement id is provisioned via the env var. */
export function isGa4Configured(): boolean {
  return isValidGa4Id(GA4_MEASUREMENT_ID);
}

/** Pure builder for the gtag.js script src (testable without the DOM). */
export function ga4ScriptUrl(measurementId: string): string {
  return `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId.trim())}`;
}

function isGa4ScriptPresent(): boolean {
  return Boolean(document.querySelector('script[data-ga4="1"]'));
}

/**
 * Load GA4 once, client-side. Idempotent (guarded on the script marker +
 * the consent default), so StrictMode's double-invoke and route remounts never
 * duplicate. No-op unless configured.
 *
 * Order: Consent Mode v2 default-DENIED (cookieless) → gtag js/config → library.
 */
export function initGa4(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!isGa4Configured() || isGa4ScriptPresent()) return;

  window.dataLayer = window.dataLayer ?? [];

  // 1. Ensure a default-denied consent state before GA4 loads (no-op if the
  //    consent stack already pushed one). Cookieless pings until a CMP grants.
  pushConsentDefaultDenied();

  // 2. Standard gtag bootstrap (via the shared shim → window.dataLayer).
  gtag("js", new Date());
  gtag("config", GA4_MEASUREMENT_ID.trim());

  // 3. Load the gtag.js library.
  const script = document.createElement("script");
  script.async = true;
  script.src = ga4ScriptUrl(GA4_MEASUREMENT_ID);
  script.dataset.ga4 = "1";
  document.head.appendChild(script);
}
